import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDataProvider, ListBase, WithListContext, useLocale, useTranslate } from 'react-admin';
import { Resources } from 'src/pages/admin/resources/resource';
import { PredictionEventItem } from '../types';
import { useSearchMarketDialogContext } from './SearchMarketDialogContext';

type EventsTabProps = {
  active: boolean;
};

const EVENTS_PAGE_SIZE = 15;

function EventsTabContent({
  data,
  isLoading,
  isPending,
  isFetching,
  error,
  total,
  offset,
  onLoadMore,
  onLoadingChange,
}: {
  data?: PredictionEventItem[];
  isLoading?: boolean;
  isPending?: boolean;
  isFetching?: boolean;
  error?: any;
  total?: number;
  offset: number;
  onLoadMore: () => void;
  onLoadingChange: (loading: boolean) => void;
}) {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { state, actions } = useSearchMarketDialogContext();
  const [expandedEventIds, setExpandedEventIds] = useState<string[]>([]);
  const [mergedResults, setMergedResults] = useState<PredictionEventItem[]>([]);
  const currentPageResults = useMemo(() => data ?? [], [data]);
  const loading = Boolean(
    isLoading || isPending || (isFetching && offset === 0 && mergedResults.length === 0)
  );
  const isFetchingMore = Boolean(isFetching && offset > 0);
  const hasReliableTotal = typeof total === 'number' && total > mergedResults.length;
  const hasMore = hasReliableTotal
    ? mergedResults.length < total
    : currentPageResults.length >= EVENTS_PAGE_SIZE;

  useEffect(() => {
    if (offset === 0) {
      setMergedResults(currentPageResults);
      return;
    }

    if (currentPageResults.length === 0) {
      return;
    }

    setMergedResults((currentResults) => {
      const nextResults = [...currentResults];
      const existingIds = new Set(currentResults.map((item) => `${item.id}`));

      currentPageResults.forEach((item) => {
        if (!existingIds.has(`${item.id}`)) {
          nextResults.push(item);
        }
      });

      return nextResults;
    });
  }, [currentPageResults, offset]);

  useEffect(() => {
    onLoadingChange(loading || isFetchingMore);
  }, [isFetchingMore, loading, onLoadingChange]);

  useEffect(() => {
    setExpandedEventIds([]);
  }, [state.searchValue]);

  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  const results = mergedResults;

  const handleToggleEvent = (eventId: string) => {
    setExpandedEventIds((currentIds) =>
      currentIds.includes(eventId)
        ? currentIds.filter((id) => id !== eventId)
        : [...currentIds, eventId]
    );
  };

  const handleSelectEvent = async (id: number) => {
    const { data: dataEn } = await dataProvider.getOne(Resources.predictionFeaturedMarketsEvents, {
      id,
      meta: { acceptLanguage: 'en' },
    });
    const { data: dataZh } = await dataProvider.getOne(Resources.predictionFeaturedMarketsEvents, {
      id,
      meta: { acceptLanguage: 'zh' },
    });

    const eventItem = {
      ...dataEn,
      title: dataEn.title,
      titleZh: dataZh.title,
    };

    actions.selectMarket({ ...eventItem, type: 'EVENT' });
  };

  const handleSelectMarket = async (id: number) => {
    const { data: dataEn } = await dataProvider.getOne(Resources.predictionFeaturedMarketsMarkets, {
      id,
      meta: { acceptLanguage: 'en' },
    });
    const { data: dataZh } = await dataProvider.getOne(Resources.predictionFeaturedMarketsMarkets, {
      id,
      meta: { acceptLanguage: 'zh' },
    });

    const marketItem = {
      ...dataEn,
      question: dataEn.question,
      questionZh: dataZh.question,
    };
    actions.selectMarket({ ...marketItem, type: 'MARKET' });
  };

  if (loading) return null;

  if (results.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate('module.featuredMarkets.noEvents')}
      </Typography>
    );
  }

  return (
    <Box
      sx={{ maxHeight: 420, overflowY: 'auto' }}
      onScroll={(event) => {
        const element = event.currentTarget;
        const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 48;

        if (isNearBottom && hasMore && !isFetchingMore) {
          onLoadMore();
        }
      }}
    >
      <List sx={{ py: 0 }}>
        {results.map((eventItem, index) => {
          const eventId = `${eventItem.id}`;
          const isExpanded = expandedEventIds.includes(eventId);
          const marketCount = eventItem.markets.length;
          const isEventAdded = state.selectedIds.has(eventId);

          return (
            <Box key={`${eventId}-${index}`}>
              <Stack direction="row" alignItems="center">
                <ListItemButton
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleEvent(eventId);
                  }}
                  sx={{ px: 0, py: 1, minWidth: 0, maxWidth: 28 }}
                >
                  {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                </ListItemButton>
                <ListItemButton
                  disabled={isEventAdded}
                  onClick={() => handleSelectEvent(eventItem.id)}
                  sx={{ px: 1, flex: 1 }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                    <Avatar
                      src={eventItem.icon || eventItem.image}
                      alt={eventItem.title}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    />
                    <ListItemText
                      primary={eventItem.title}
                      secondary={
                        isEventAdded
                          ? translate('module.featuredMarkets.alreadyAdded')
                          : eventItem.slug || `${marketCount} market${marketCount === 1 ? '' : 's'}`
                      }
                    />
                  </Stack>
                </ListItemButton>
              </Stack>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 4, pb: 1 }}>
                  {marketCount === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      {translate('module.featuredMarkets.noMarketsInEvent')}
                    </Typography>
                  ) : (
                    <List sx={{ py: 0 }}>
                      {eventItem.markets.map((marketItem, marketIndex) => {
                        const isAdded = state.selectedIds.has(`${marketItem.id}`);

                        return (
                          <Box key={`${eventId}-${marketItem.id}-${marketIndex}`}>
                            <ListItemButton
                              disabled={isAdded}
                              onClick={() => handleSelectMarket(marketItem.id)}
                              sx={{ px: 0 }}
                            >
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                sx={{ width: '100%' }}
                              >
                                <Avatar
                                  src={marketItem.icon || marketItem.image}
                                  alt={marketItem.question}
                                  variant="rounded"
                                  sx={{ width: 36, height: 36 }}
                                />
                                <ListItemText
                                  primary={marketItem.question}
                                  secondary={
                                    isAdded
                                      ? translate('module.featuredMarkets.alreadyAdded')
                                      : marketItem.slug || `ID: ${marketItem.id}`
                                  }
                                />
                              </Stack>
                            </ListItemButton>
                            {marketIndex < eventItem.markets.length - 1 ? <Divider /> : null}
                          </Box>
                        );
                      })}
                    </List>
                  )}
                </Box>
              </Collapse>

              {index < results.length - 1 ? <Divider /> : null}
            </Box>
          );
        })}

        {isFetchingMore ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0, py: 2 }}>
            {translate('module.featuredMarkets.loading')}
          </Typography>
        ) : null}
      </List>
    </Box>
  );
}

export default function EventsTab({ active }: EventsTabProps) {
  const locale = useLocale();
  const { state } = useSearchMarketDialogContext();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (active) {
      setOffset(0);
    }
  }, [active, state.searchValue]);

  if (!active) return null;

  return (
    <ListBase
      resource={Resources.predictionFeaturedMarketsEvents}
      filter={{ q: state.searchValue, offset: `${offset}` }}
      perPage={EVENTS_PAGE_SIZE}
      queryOptions={{ meta: { acceptLanguage: locale }, retry: false }}
      sort={{ field: 'id', order: 'ASC' }}
      storeKey={false}
      disableSyncWithLocation
    >
      <WithListContext<PredictionEventItem>
        render={(context) => (
          <EventsTabContent
            {...context}
            offset={offset}
            onLoadingChange={() => {}}
            onLoadMore={() => setOffset((currentOffset) => currentOffset + EVENTS_PAGE_SIZE)}
          />
        )}
      />
    </ListBase>
  );
}
